"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

/**
 * Barre d'onglets sticky façon Doctolib.
 *
 * - Affiche les catégories de la fiche médecin (L'essentiel, Carte, Présentation, Horaires, Tarifs, Avis)
 * - Scroll spy via IntersectionObserver : surligne automatiquement la section visible
 * - Scroll horizontal sur mobile, étiré sur desktop
 * - Comportement Doctolib : sticky sous le hero, smooth scroll vers l'ancre, padding-top pour compenser le header global
 */

export type TabItem = {
  id: string;
  label: string;
  /** Si true, l'onglet est masqué sur mobile (lg:hidden retiré, hidden sm:flex) */
  desktopOnly?: boolean;
};

type Props = {
  tabs: TabItem[];
  /** offset (px) appliqué pour la détection IntersectionObserver — typiquement la hauteur du header global + cette barre */
  scrollOffset?: number;
};

export function StickyTabNav({ tabs, scrollOffset = 120 }: Props) {
  // 120 = patient header h-14 (56px) + cette nav h-12/sm:h-14 (~56px) + 8px marge.
  // Avant : 140 (calé sur l'ancien header h-[72px], laissait un gap de 16px sous
  // le header sticky où le contenu bleedait à travers, et faisait passer les
  // sections sticky aside #rdv sous le tab nav).
  const tNav = useTranslations("physician.stickyTabs");
  const [active, setActive] = useState<string>(tabs[0]?.id ?? "");
  const navRef = useRef<HTMLDivElement | null>(null);
  // Ignore les mises à jour de l'observer pendant le smooth-scroll déclenché
  // par un clic — sur un aller court (ex: Essentiel → Carte, sections
  // adjacentes depuis le réordonnancement du 2026-07-21), l'observer peut ne
  // capter qu'un état transitoire et laisser l'onglet précédent actif après
  // que le scroll se soit déjà stabilisé sur la bonne section.
  const suppressObserverUntilRef = useRef<number>(0);

  useEffect(() => {
    const sections = tabs
      .map((t) => document.getElementById(t.id))
      .filter((el): el is HTMLElement => el !== null);

    if (sections.length === 0) return;

    // SÉCURITÉ NAVIGATEUR (Sentry SAHHA-2G/2H, 2026-09-30) : certaines WebViews
    // d'applications tierces et navigateurs in-app n'exposent pas
    // IntersectionObserver, et l'instancier y lève "Can't find variable".
    // Ce scrollspy est purement décoratif : sans lui la navigation reste
    // entièrement utilisable, seul le surlignage de la section active est perdu.
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < suppressObserverUntilRef.current) return;
        // Prend la section la plus visible dans le viewport (au-dessus du seuil)
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          setActive(visible[0].target.id);
        }
      },
      {
        // marge négative en haut pour ignorer la zone cachée derrière le header sticky
        rootMargin: `-${scrollOffset}px 0px -55% 0px`,
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [tabs, scrollOffset]);

  // Quand le tab actif change, scroll horizontalement la nav (mobile) — sans toucher le scroll vertical du document.
  useEffect(() => {
    if (!navRef.current) return;
    const nav = navRef.current;
    const el = nav.querySelector<HTMLAnchorElement>(`a[data-tab="${active}"]`);
    if (!el || nav.scrollWidth <= nav.clientWidth) return;
    // Calcul manuel du scrollLeft pour centrer le tab actif horizontalement,
    // évite scrollIntoView({block:"nearest"}) qui pouvait scroller le document
    // verticalement (et faire remonter la page quand on atteignait Tarifs/Avis).
    const navRect = nav.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset = elRect.left - navRect.left + nav.scrollLeft - (nav.clientWidth - el.clientWidth) / 2;
    nav.scrollTo({ left: offset, behavior: "smooth" });
  }, [active]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (target) {
      // Optimiste : l'onglet cliqué devient actif immédiatement, l'observer
      // reste muet le temps du smooth-scroll (cf. suppressObserverUntilRef)
      // puis reprend la main pour suivre le scroll naturel de l'utilisateur.
      setActive(id);
      suppressObserverUntilRef.current = Date.now() + 700;
      const top = target.getBoundingClientRect().top + window.scrollY - scrollOffset + 12;
      window.scrollTo({ top, behavior: "smooth" });
      // Met à jour l'URL sans déclencher de scroll natif
      history.replaceState(null, "", `#${id}`);
    }
  };

  return (
    <div className="sticky top-[calc(48px+env(safe-area-inset-top,0px))] z-20 -mx-4 border-b border-[var(--sahha-border-subtle)] bg-white/95 backdrop-blur-md lg:top-14 sm:mx-0">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav
          ref={navRef}
          aria-label={tNav("ariaLabel")}
          className="scrollbar-none flex gap-1 overflow-x-auto"
        >
          {tabs.map((t) => {
            const isActive = active === t.id;
            return (
              <a
                key={t.id}
                href={`#${t.id}`}
                data-tab={t.id}
                onClick={(e) => handleClick(e, t.id)}
                className={`relative flex h-12 shrink-0 items-center px-4 text-[14px] font-semibold transition-colors sm:h-14 sm:px-5 sm:text-[15px] ${
                  t.desktopOnly ? "hidden lg:flex" : ""
                } ${
                  isActive
                    ? "text-[var(--sahha-primary-700)]"
                    : "text-[var(--sahha-text-600)] hover:text-[var(--sahha-text-900)]"
                }`}
              >
                {t.label}
                <span
                  aria-hidden
                  className={`absolute bottom-0 left-3 right-3 h-[3px] rounded-t-full transition-all sm:left-4 sm:right-4 ${
                    isActive ? "bg-[var(--sahha-primary-600)]" : "bg-transparent"
                  }`}
                />
              </a>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
